import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import styles from "./Courses.module.css";

export default function Courses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [joinedCourses, setJoinedCourses] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation("global");
  const [isLoading, setIsLoading] = useState(false);
  const [bgImage, setBgImage] = useState("");
  const userData = JSON.parse(localStorage.getItem("userDataLogin"));
  const token = userData?.token;
  const lang = localStorage.getItem("lang")?.replace(/"/g, "") || "en";

  useEffect(() => {
    fetchCourses(currentPage);
    // Only fetch joined courses for non-teacher users
    if (token && userData?.user?.type !== "student" && userData?.user?.type !== "teacher") {
      fetchJoinedCourses();
    }
  }, [currentPage]);

  const fetchCourses = async (page) => {
    let endpoint;
    
    // Check if user is a teacher
    if (token && userData?.user?.type === "teacher") {
      // For teachers, use the instructor-courses endpoint
      endpoint = `${process.env.REACT_APP_API_URL}/instructor-courses`;
    } else {
      // For regular users, use the existing endpoints
      endpoint = token
        ? `${process.env.REACT_APP_API_URL}/courses?page=${page}`
        : `${process.env.REACT_APP_API_URL}/random_course?page=${page}`;
    }

    try {
      const response = await fetch(endpoint, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          "Accept-Language": lang,
        },
      });

      const result = await response.json();
      
      if (token && userData?.user?.type === "teacher") {
        // For teachers, the data structure is different
        setCourses(result?.data?.data || []);
        setPagination(result?.data?.meta || {});
      } else {
        // For regular users, use existing logic
        setCourses(result?.data || []);
        setPagination(result?.pagination || {});
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      // Removed toast error to avoid endpoint issues
    }
  };

  const fetchJoinedCourses = async () => {
    if (userData?.user?.type === "student") return;
  
    setIsLoading(true); // ابدأ التحميل
  
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/joint_course`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": lang,
          },
        }
      );
      const result = await response.json();
      setJoinedCourses(result?.data || []);
    } catch (error) {
      console.error("Error fetching joined courses:", error);
      // Removed toast error to avoid endpoint issues
    } finally {
      setIsLoading(false); // انتهى التحميل
    }
  };
  

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      setCurrentPage(page);
    }
  };

  const handleCardClick = async (course) => {
    if (!token) {
      navigate("/login");
      return;
    }

    // If user is a teacher, navigate directly to CourseDetails
    if (userData?.user?.type === "teacher") {
      navigate(`/CourseDetails/${course.id}`);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payment_course/${course.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (course.type === "free") {
        if (result?.code === 200 || result?.status === 200) {
          navigate(`/CourseDetails/${course.id}`);
          // Removed toast success to avoid endpoint issues
        } else {
          // Removed toast error to avoid endpoint issues
        }
      } else if (course.type === "paid") {
        if (course.purshed === 1) {
          navigate(`/CourseDetails/${course.id}`);
          // Removed toast success to avoid endpoint issues
        } else {
          if (result?.redirect_url) {
            window.location.href = result.redirect_url;
          } else {
            console.error("Redirect URL Missing:", result);
            // Removed toast error to avoid endpoint issues
          }
        }
      }
    } catch (error) {
      // Removed toast error to avoid endpoint issues
      console.error("Error:", error);
    }
  };
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/setting`);
        const result = await response.json();
        if (result?.data[0]?.image_courses) {
          setBgImage(result.data[0].image_courses);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };

    fetchSettings();
  }, []);

  return (
    <>
      <div className={`${styles.coursesHero} custom-section`} style={{
        backgroundImage: `url(${bgImage})`,
      }}>
        <div className="container">
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>{t('header.courses')}</h1>
            <h6 className={styles.heroSubtitle}>
              <Link className={styles.heroLink} to="/">{t('header.home')}</Link>
              <span> - </span>
              <span className={styles.secondaryColor}>{t('header.courses')}</span>
            </h6>
          </div>
        </div>
      </div>

      <section className={`${styles.mainSection} container my-5`}>
        <div className={`container ${styles.coursesGrid}`}>
        <div className="row justify-content-center">
  {isLoading ? (
    <p className={styles.loadingText}>{t("loading")}</p>
  ) : courses.length === 0 ? (
    <div className="col-12 text-center">
      <p className={styles.noCoursesText}>
        {userData?.user?.type === "teacher" 
          ? "لا توجد كورسات متاحة حالياً" 
          : t("noCourses")}
      </p>
    </div>
  ) : (
    // For teachers, show only courses. For others, show joinedCourses + courses
    (userData?.user?.type === "teacher" ? courses : [...joinedCourses, ...courses]).map((course, index) => {
      let courseImage = "";
      try {
        const images = JSON.parse(course.image);
        courseImage = images[0];
      } catch {
        courseImage = course.image;
      }

      const instructor = course.instructors?.[0];

      return (
        <div
          key={course.id || index}
          className="col-md-4 mb-5"
          onClick={() => handleCardClick(course)}
        >
          <div className={`${styles.courseCard} custom-card`}>
            <img src={courseImage} alt="Course" className={`${styles.courseImage} card-img`} />
            <div className={styles.cardBody}>
              <div className={styles.courseInfo}>
                <span>
                  <i className={`bi bi-journal-text ${styles.courseInfoIcon}`}></i>
                  {course.lessons} {t('onlineCourses2.lessons')}
                </span>
              </div>
              <h3 className={styles.courseTitle}>{course.name}</h3>
              
              {/* Stage and Grade Info */}
              {(course.stage || course.grade) && (
                <div className={styles.courseDetails}>
                  {course.stage && (
                    <div className={styles.courseDetail}>
                      <i className={`bi bi-mortarboard ${styles.detailIcon}`}></i>
                      <span>{course.stage}</span>
                    </div>
                  )}
                  {course.grade && (
                    <div className={styles.courseDetail}>
                      <i className={`bi bi-book ${styles.detailIcon}`}></i>
                      <span>{course.grade}</span>
                    </div>
                  )}
                </div>
              )}
              
              {instructor && (
                <div className={styles.courseInstructor}>
                  <img src={instructor.image} alt={instructor.name} className={styles.instructorImage} />
                  <span className={styles.instructorName}>{instructor.name}</span>
                </div>
              )}
              <div className={styles.courseFooter}>
                <span className={styles.price}>
                  {course.type === "free" ? t('free') : course.price}
                </span>
                <span className={styles.details}>
                  {userData?.user?.type === "teacher" 
                    ? t('view') 
                    : course.type === "free"
                      ? t('join')
                      : course.purshed === 1
                        ? t('join')
                        : t('subscribe')}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    })
  )}
</div>


          {/* Pagination Section */}
          {pagination?.total > 0 && (
            <nav aria-label="Page navigation example" className={styles.paginationContainer}>
              <ul className={`pagination ${styles.pagination} justify-content-center mt-4`}>
                <li className={`page-item ${styles.pageItem} ${pagination.current_page === 1 ? "disabled" : ""}`}>
                  <button className={`page-link ${styles.pageLink}`} onClick={() => handlePageChange(currentPage - 1)}>
                    Prev
                  </button>
                </li>

                {Array.from({ length: pagination.last_page }, (_, index) => (
                  <li
                    key={index}
                    className={`page-item ${styles.pageItem} ${pagination.current_page === index + 1 ? "active" : ""}`}
                  >
                    <button className={`page-link ${styles.pageLink}`} onClick={() => handlePageChange(index + 1)}>
                      {index + 1}
                    </button>
                  </li>
                ))}

                <li className={`page-item ${styles.pageItem} ${pagination.current_page === pagination.last_page ? "disabled" : ""}`}>
                  <button className={`page-link ${styles.pageLink}`} onClick={() => handlePageChange(currentPage + 1)}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}
